import type { ComposerAttributeNode } from '../../../configurator-components/composer-editors/attributes/composerAttributesModels';

const ATTRIBUTE = 'Attribute';
const GROUP = 'AttributeGroup';
const STEP = 'AttributeStep';
const PRESET = 'PreSetContainer';

export interface ComposerStructureSelectionRules {
    canDisable: boolean;
    canDuplicate: boolean;
    canEnable: boolean;
    canManageLinks: boolean;
    canRetarget: boolean;
}

export interface ComposerStructureSelectionSummary {
    attributes: number;
    groups: number;
    presetConfigurations: number;
    steps: number;
}

export const getComposerStructureSelectionRules = (selection: Pick<ComposerAttributeNode, 'enabled' | 'type'>[]): ComposerStructureSelectionRules => {
    const firstType = selection[0]?.type;
    const isHomogeneous = selection.length > 0 && selection.every((item) => item.type === firstType);

    return {
        canDisable: selection.some((item) => item.type !== PRESET && item.enabled !== false),
        canDuplicate: isHomogeneous && (firstType === ATTRIBUTE || firstType === GROUP || firstType === STEP || firstType === PRESET),
        canEnable: selection.some((item) => item.type !== PRESET && item.enabled === false),
        canManageLinks: selection.length > 0 && selection.every((item) => item.type === ATTRIBUTE),
        canRetarget: selection.length > 0 && selection.every((item) => item.type === ATTRIBUTE || item.type === GROUP)
    };
};

export const getComposerSelectionDescendants = (selection: Pick<ComposerAttributeNode, 'id'>[], allRows: ComposerAttributeNode[]): ComposerAttributeNode[] => {
    const selectedIDs = new Set(selection.map((item) => item.id));
    const visitedIDs = new Set<string>();
    const descendants: ComposerAttributeNode[] = [];

    const visit = (parentID: string) => {
        allRows
            .filter((candidate) => candidate.parentID === parentID)
            .forEach((child) => {
                if (selectedIDs.has(child.id) || visitedIDs.has(child.id)) return;
                visitedIDs.add(child.id);
                descendants.push(child);
                visit(child.id);
            });
    };

    selection.forEach((item) => visit(item.id));
    return descendants;
};

export const getComposerStructureSelectionSummary = (selection: Pick<ComposerAttributeNode, 'type'>[]): ComposerStructureSelectionSummary => ({
    attributes: selection.filter((item) => item.type === ATTRIBUTE).length,
    groups: selection.filter((item) => item.type === GROUP).length,
    presetConfigurations: selection.filter((item) => item.type === PRESET).length,
    steps: selection.filter((item) => item.type === STEP).length
});
